import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { useStoreState } from "ezStore";

import { zodResolver } from "@hookform/resolvers/zod";

import { Input, FormGroup, Checkbox } from "@cofano/cck";
import { IconButton, Tooltip } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import NumberInput from "components/cck/NumberInput";
import { useConfirm } from "components/hooks/useConfirm";

import { Fields, hasOptionalFieldPermission, Language, usePermission } from "imports/Utils";
import { round } from "utils/numberUtils";

import LocationSelect from "view/locations/components/selects/LocationSelect";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import ProductSelect from "view/products/components/select/ProductSelect";
import { CargoFromCallSideviewer } from "view/calls/components/sideviewer/useCallSideviewer";
import { updateCargoFieldsSchema, UpdateCargoFieldsSchemaInput, UpdateCargoFieldsSchemaOutput } from "view/cargos/utils/schema";

import { SaveCargoFormDocument } from "__generated__/view/cargos/graphql/cargo.mutations.generated";
import { CallType, RelationType } from "__generated__/enums";

import moment from "moment";

const UpdateCargoActionFieldsDialog = ({
	open,
	cargo,
	voyageId,
	callId,
	onClose,
	onSave,
}: {
	open: boolean;
	cargo: CargoFromCallSideviewer
	voyageId: number;
	callId: number;
	onClose: () => void;
	onSave: () => void;
}) => {

	const session = useStoreState(store => store.session);

	const { isSkipperOrShipManager } = usePermission();
	const confirm = useConfirm();

	const [ linkedFields, setLinkedFields ] = useState(true);
	const [ applyWeightAndVolumeOnOtherAction, setApplyWeightAndVolumeOnOtherAction ] = useState(true);

	const [ saveCargo, { loading } ] = useMutation(SaveCargoFormDocument);

	const defaultValues = {
		id: cargo.id,
		loadingReference: cargo.loadingReference ?? "",
		dischargingReference: cargo.dischargingReference ?? "",
		supplier: cargo.supplier ?? null,
		product: cargo.product ?? null,
		actions: cargo.actions.map(action => ({
			id: action.id,
			type: action.type,
			location: action.location,
			weight: action.weight ?? null,
			volume: action.volume ?? null,
		})),
	};

	const { control, handleSubmit, reset, setValue, watch } = useForm<UpdateCargoFieldsSchemaInput, unknown, UpdateCargoFieldsSchemaOutput>({
		resolver: zodResolver(updateCargoFieldsSchema),
		defaultValues: defaultValues,
	});

	const [ product, actions ] = watch([ "product", "actions" ]);

	const loadingActions = cargo.actions.filter(c => c.type === CallType.Loading);
	const dischargingActions = cargo.actions.filter(c => c.type === CallType.Discharging);
	const enableChangingOtherAction = cargo.actions.length === 2;

	const currentAction = cargo.actions.find(a => callId === a.call?.id);
	const currentActionIndex = cargo.actions.findIndex(a => a.id == currentAction?.id);

	const handleOnClose = () => {
		onClose();
		setLinkedFields(true);
		setApplyWeightAndVolumeOnOtherAction(true);
	};

	const onWeightChange = (value: number | null) => {
		if (loadingActions.length === 1 && dischargingActions.length === 1) {
			setValue("actions.0.weight", value);
			setValue("actions.1.weight", value);
		} else {
			setValue(`actions.${currentActionIndex}.weight`, value);
		}

		const densityKgM3 = product?.densityKgM3 || 1;
		const volume = round((value ?? 0) / densityKgM3, 3);

		if (linkedFields && densityKgM3 && densityKgM3 > 0) {
			if (loadingActions.length === 1 && dischargingActions.length === 1) {
				setValue("actions.0.volume", volume);
				setValue("actions.1.volume", volume);
			}

			setValue(`actions.${currentActionIndex}.volume`, volume);
		}
	};

	const onVolumeChange = (value: number | null) => {
		if (loadingActions.length === 1 && dischargingActions.length === 1) {
			setValue("actions.0.volume", value);
			setValue("actions.1.volume", value);
		} else {
			setValue(`actions.${currentActionIndex}.volume`, value);
		}

		const densityKgM3 = product?.densityKgM3 || 1;
		const weight = round((value ?? 0) * densityKgM3, 3);

		if (linkedFields && densityKgM3 && densityKgM3 > 0) {
			if (loadingActions.length === 1 && dischargingActions.length === 1) {
				setValue("actions.0.weight", weight);
				setValue("actions.1.weight", weight);
			}

			setValue(`actions.${currentActionIndex}.weight`, weight);
		}
	};

	const validateAndSave = (data: UpdateCargoFieldsSchemaOutput) => {
		const [ netWeight, netVolume ] = data.actions.reduce((acc, current) => {
			let [ netWeight, netVolume ] = acc;
			netWeight = netWeight + (current.weight ?? 0) * (current.type == CallType.Loading ? 1 : -1);
			netVolume = netVolume + (current.volume ?? 0) * (current.type == CallType.Loading ? 1 : -1);
			return [ netWeight, netVolume ];
		}, [ 0, 0 ]);

		if (!netWeight && !netVolume) {
			save(data);
		} else {
			confirm({
				type: "WARNING",
				title: intl.formatMessage({ id: "LOADING_DISCHARGING_ACTIONS_DONT_MATCH" }),
			}).then(() => {
				save(data);
			});
		}
	};

	const save = (data: UpdateCargoFieldsSchemaOutput) => {

		const input = {
			id: data.id,
			product: {
				id: data.product.id,
			},
			productDetails: cargo.productDetails ?? undefined,
			loadingReference: data.loadingReference || undefined,
			dischargingReference: data.dischargingReference || undefined,
			actions: data.actions.map(action => {
				const currentAction = cargo.actions.find(a => a.id === action.id);
				return {
					id: action.id,
					cargo: data.id,
					location: {
						id: action.location.id,
					},
					weight: action.weight,
					volume: action.volume,
					type: action.type,
					date: !currentAction?.displayAsWindow ? moment(currentAction?.start).format("YYYY-MM-DD") : null,
					windowFrom: currentAction?.displayAsWindow ? currentAction.start : null,
					windowTo: currentAction?.displayAsWindow ? currentAction.end : null,
					measurements: {
						...(currentAction?.measurements ?? {}),
						relativeDensity: currentAction?.measurements?.relativeDensity ?? null,
						productTemperature: currentAction?.measurements?.productTemperature ?? null,
					},
				};
			}),
			supplier: data.supplier ? {
				id: data.supplier.id,
				name: data.supplier.name,
				isApplicationRelation: data.supplier.applicationRelation,
			} : undefined,
			deliveryCondition: cargo.deliveryCondition ? {
				id: cargo.deliveryCondition.id,
			} : undefined,
			charter: cargo.charter ? {
				id: cargo.charter.id,
				type: cargo.charter.type,
			} : undefined,
			voyage: {
				id: voyageId,
			},
		};

		if (enableChangingOtherAction && applyWeightAndVolumeOnOtherAction) {
			const weight = input.actions[currentActionIndex]?.weight;
			const volume = input.actions[currentActionIndex]?.volume;
			const otherActionIndex = cargo?.actions?.findIndex(a => a.id !== currentAction?.id);
			input.actions[otherActionIndex].weight = weight;
			input.actions[otherActionIndex].volume = volume;
		}

		saveCargo({
			variables: {
				input: input,
			},
		}).then(() => {
			handleOnClose();
			onSave();
		});
	};

	useEffect(() => {
		if (open && cargo) {
			reset(defaultValues);
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CARGO" })}
			form='updateCargoFieldsForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(validateAndSave)(event)} id="updateCargoFieldsForm">
				<FormGroup
					label={Language("LOCATION")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name={`actions.${currentActionIndex}.location`}
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<LocationSelect
									{...field}
									multiple={false} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				{actions[currentActionIndex]?.type === CallType.Loading && (
					<FormGroup label={Language("LOADING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="loadingReference"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>
				)}

				{actions[currentActionIndex]?.type === CallType.Discharging && (
					<FormGroup label={Language("DISCHARGING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="dischargingReference"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>
				)}

				{hasOptionalFieldPermission(session, Fields.ORDER, "supplier") && (
					<FormGroup label={Language("SUPPLIER")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="supplier"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<RelationSelect
										{...field}
										types={[ RelationType.Supplier ]}
										onChange={option => field.onChange(option ? { id: option.id, name: option.name } : null)}
										disabled={isSkipperOrShipManager} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>
				)}

				<FormGroup
					label={Language("PRODUCT")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name='product'
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<ProductSelect {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />

				</FormGroup>

				{hasOptionalFieldPermission(session, Fields.ORDER, "weight") && (
					<FormGroup label={Language("ORDER_WEIGHT")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name={`actions.${currentActionIndex}.weight`}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<NumberInput
										{...field}
										onChange={value => onWeightChange(value)}
										decimalScale={3} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>
				)}

				{hasOptionalFieldPermission(session, Fields.ORDER, "weight") && hasOptionalFieldPermission(session, Fields.ORDER, "volume") && (
					<Tooltip title={intl.formatMessage({ id: "CALCULATE_FIELDS_AUTOMATICALLY" })}>
						<span>
							<IconButton onClick={() => setLinkedFields(!linkedFields)}>
								{linkedFields ? <LinkIcon /> : <LinkOffIcon color="disabled" />}
							</IconButton>
						</span>
					</Tooltip>
				)}

				{hasOptionalFieldPermission(session, Fields.ORDER, "volume") && (
					<FormGroup label={Language("ORDER_VOLUME")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name={`actions.${currentActionIndex}.volume`}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<NumberInput
										{...field}
										onChange={value => onVolumeChange(value)}
										decimalScale={3} />

									{enableChangingOtherAction && (
										<Checkbox
											checked={applyWeightAndVolumeOnOtherAction}
											onChange={event => setApplyWeightAndVolumeOnOtherAction(event.target.checked)}
											label={intl.formatMessage({ id: "APPLY_WEIGHT_VOLUME_ON_OTHER_ACTION" })} />
									)}

									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>
				)}
			</form>
		</FormDialog>
	);
};

export default UpdateCargoActionFieldsDialog;
